<template>
	<div class="dashboard-root">
		<v-row v-if="showStudentRegistrationSectorCard">
			<v-col cols="12">
				<v-card class="dashboard-widget-card" elevation="2">
					<v-toolbar flat color="primary" dark dense class="dashboard-widget-toolbar">
						<v-icon class="mr-2">mdi-school-outline</v-icon>
						<v-toolbar-title class="font-weight-medium">
							Inschrijvingen komend schooljaar (per sector)
						</v-toolbar-title>
					</v-toolbar>
					<v-card-text v-if="registrationSchoolyearCaption" class="py-2 pb-0 caption text--secondary">
						{{ registrationSchoolyearCaption }}
					</v-card-text>
					<v-divider />
					<v-card-text class="pa-0">
						<v-alert
							v-if="registrationError"
							type="error"
							dense
							text
							border="left"
							colored-border
							class="ma-4 mb-0"
						>
							{{ registrationError }}
						</v-alert>
						<v-data-table
							v-else
							:headers="registrationSectorHeaders"
							:items="registrationSectorRows"
							:loading="registrationLoading"
							item-key="sector_key"
							hide-default-footer
							disable-pagination
							dense
							class="dashboard-data-table elevation-0 dashboard-table-fullwidth"
							no-data-text="Geen inschrijvingen voor dit schooljaar in de geselecteerde scope."
						>
							<template v-slot:item.bar="{ item }">
								<div class="sector-bar-wrap">
									<v-progress-linear
										:value="registrationBarPercent(item.count)"
										color="primary"
										height="8"
										rounded
									/>
								</div>
							</template>
						</v-data-table>
						<div
							v-if="!registrationLoading && !registrationError && registrationTotal != null"
							class="px-4 py-3 body-2"
						>
							<strong>Totaal aantal inschrijvingen:</strong> {{ registrationTotal }}
						</div>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<v-row v-if="showStudentRegistrationSectorCard" class="mt-2">
			<v-col cols="12">
				<v-card class="dashboard-widget-card" elevation="2">
					<v-toolbar flat color="primary" dark dense class="dashboard-widget-toolbar">
						<v-icon class="mr-2">mdi-bookshelf</v-icon>
						<v-toolbar-title class="font-weight-medium">
							Inschrijvingen komend schooljaar (per sector en opleiding)
						</v-toolbar-title>
					</v-toolbar>
					<v-card-text v-if="registrationSchoolyearCaption" class="py-2 pb-0 caption text--secondary">
						{{ registrationSchoolyearCaption }}
					</v-card-text>
					<v-divider />
					<v-card-text class="pa-0">
						<v-alert
							v-if="registrationStudyError"
							type="error"
							dense
							text
							border="left"
							colored-border
							class="ma-4 mb-0"
						>
							{{ registrationStudyError }}
						</v-alert>
						<div v-else-if="registrationStudyLoading && registrationSectorStudyBlocks.length === 0" class="pa-8 text-center">
							<v-progress-circular indeterminate color="primary" size="40" />
						</div>
						<div
							v-else-if="!registrationStudyLoading && registrationSectorStudyBlocks.length === 0"
							class="pa-6 body-2 text--secondary"
						>
							Geen inschrijvingen voor dit schooljaar in de geselecteerde scope.
						</div>
						<div v-else class="sector-study-groups pa-2">
							<div
								v-for="block in registrationSectorStudyBlocks"
								:key="block.sector_key"
								class="sector-study-group mb-4"
							>
								<div class="sector-study-group__title subtitle-2 font-weight-medium primary--text px-2 py-2">
									{{ block.sector_name }}
									<span class="text--secondary font-weight-regular">— {{ block.sector_total }} inschrijvingen</span>
								</div>
								<v-data-table
									:headers="registrationPerSectorStudyHeaders"
									:items="block.study_rows"
									:loading="false"
									item-key="row_key"
									hide-default-footer
									disable-pagination
									dense
									class="dashboard-data-table elevation-0 dashboard-table-fullwidth sector-nested-table"
									:mobile-breakpoint="0"
								>
									<template v-slot:item.study_name="{ item }">
										<span class="study-name-cell">{{ item.study_name }}</span>
									</template>
									<template v-slot:item.bar="{ item }">
										<div class="sector-bar-wrap">
											<v-progress-linear
												:value="studyBarPercentInSector(item.count, block.study_max)"
												color="primary"
												height="8"
												rounded
											/>
										</div>
									</template>
								</v-data-table>
							</div>
						</div>
						<div
							v-if="!registrationStudyLoading && !registrationStudyError && registrationStudyTotal != null"
							class="px-4 py-3 body-2"
						>
							<strong>Totaal aantal inschrijvingen:</strong> {{ registrationStudyTotal }}
						</div>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../config";
import {
	isDashboardBlockedRole,
	isDashboardCoordinatorRole,
	isDashboardRegistrationsRole,
	effectiveDashboardRoleId,
	dashboardCoordinatorIdentity,
	dashboardBlockedRedirectPath
} from "../router/authMeta";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 60000
});

export default {
	name: "dashboard_registrations",
	data() {
		return {
			dashboardUser: null,
			registrationBySector: [],
			registrationTotal: null,
			registrationSchoolyearName: null,
			registrationLoading: false,
			registrationError: null,
			registrationSectorsWithStudies: [],
			registrationStudyTotal: null,
			registrationStudyLoading: false,
			registrationStudyError: null,
			registrationSectorHeaders: [
				{ text: "Sector", value: "sector_name", sortable: true },
				{ text: "Aantal inschrijvingen", value: "count", sortable: true, width: "140px" },
				{ text: "", value: "bar", sortable: false, width: "45%" }
			],
			registrationPerSectorStudyHeaders: [
				{ text: "Opleiding", value: "study_name", sortable: false },
				{ text: "Aantal inschrijvingen", value: "count", sortable: false, width: "140px" },
				{ text: "", value: "bar", sortable: false, width: "40%" }
			]
		};
	},
	computed: {
		dashboardRoleId() {
			return effectiveDashboardRoleId(this.dashboardUser);
		},
		showStudentRegistrationSectorCard() {
			return isDashboardRegistrationsRole(this.dashboardRoleId);
		},
		registrationSchoolyearCaption() {
			var parts = [];
			if (this.registrationSchoolyearName) {
				parts.push(this.registrationSchoolyearName);
			}
			if (isDashboardCoordinatorRole(this.dashboardRoleId)) {
				parts.push("Alleen opleidingen waarvoor u coördinator bent");
			}
			return parts.join(" · ");
		},
		registrationSectorRows() {
			return (this.registrationBySector || []).map(function(row) {
				return {
					sector_key: row.sector_id != null ? "r" + row.sector_id : "_none",
					sector_name: row.sector_name || "—",
					count: row.count,
					bar: row.count
				};
			});
		},
		maxRegistrationSectorCount() {
			var rows = this.registrationBySector || [];
			if (!rows.length) {
				return 0;
			}
			return Math.max.apply(
				null,
				rows.map(function(r) {
					return r.count || 0;
				})
			);
		},
		registrationSectorStudyBlocks() {
			return (this.registrationSectorsWithStudies || []).map(function(sec, si) {
				var studies = sec.studies || [];
				var studyMax =
					studies.length > 0
						? Math.max.apply(
								null,
								studies.map(function(s) {
									return s.count || 0;
								})
						  )
						: 0;
				return {
					sector_key: sec.sector_id != null ? "sec" + sec.sector_id : "_none_" + si,
					sector_name: sec.sector_name || "—",
					sector_total: typeof sec.total === "number" ? sec.total : 0,
					study_max: studyMax,
					study_rows: studies.map(function(st, ti) {
						return {
							row_key:
								(sec.sector_id != null ? String(sec.sector_id) : "x") +
								"_" +
								ti +
								"_" +
								String(st.study_name || ""),
							study_name: st.study_name || "—",
							count: st.count
						};
					})
				};
			});
		}
	},
	created() {
		try {
			this.dashboardUser = JSON.parse(localStorage.getItem("user") || "null");
		} catch (e) {
			this.dashboardUser = null;
		}
		var rid = this.dashboardRoleId;
		if (isDashboardBlockedRole(rid)) {
			this.$router.replace({ path: dashboardBlockedRedirectPath(rid), replace: true });
			return;
		}
		if (this.showStudentRegistrationSectorCard) {
			this.loadRegistrationDashboardWidgets();
		}
	},
	methods: {
		dashboardAuthParams() {
			var p = {};
			if (this.dashboardRoleId != null && !Number.isNaN(this.dashboardRoleId)) {
				p.role_id = this.dashboardRoleId;
			}
			var un = dashboardCoordinatorIdentity(this.dashboardUser);
			if (un) {
				p.username = un;
			}
			return p;
		},
		loadRegistrationDashboardWidgets() {
			var self = this;
			var params = self.dashboardAuthParams();
			self.registrationLoading = true;
			self.registrationStudyLoading = true;
			self.registrationError = null;
			self.registrationStudyError = null;
			ax.get("/dashboard/registration_counts_by_sector", { params: params })
				.then(function(response) {
					var d = response.data || {};
					self.registrationBySector = d.by_sector || [];
					self.registrationTotal = typeof d.total === "number" ? d.total : null;
					if (d.schoolyear_name) {
						self.registrationSchoolyearName = d.schoolyear_name;
					}
					self.registrationLoading = false;
				})
				.catch(function(err) {
					self.registrationLoading = false;
					self.registrationBySector = [];
					self.registrationTotal = null;
					self.registrationError =
						(err.response && err.response.data && err.response.data.error) ||
						"Kon inschrijvingen per sector niet laden.";
				});
			ax.get("/dashboard/registration_counts_by_study", { params: params })
				.then(function(response) {
					var ds = response.data || {};
					self.registrationSectorsWithStudies = ds.sectors || [];
					self.registrationStudyTotal = typeof ds.total === "number" ? ds.total : null;
					if (ds.schoolyear_name && !self.registrationSchoolyearName) {
						self.registrationSchoolyearName = ds.schoolyear_name;
					}
					self.registrationStudyLoading = false;
				})
				.catch(function(err) {
					self.registrationStudyLoading = false;
					self.registrationSectorsWithStudies = [];
					self.registrationStudyTotal = null;
					self.registrationStudyError =
						(err.response && err.response.data && err.response.data.error) ||
						"Kon inschrijvingen per sector en opleiding niet laden.";
				});
		},
		registrationBarPercent(count) {
			var max = this.maxRegistrationSectorCount;
			if (!max || !count) {
				return 0;
			}
			return Math.round((count / max) * 100);
		},
		studyBarPercentInSector(count, sectorMax) {
			if (!sectorMax || !count) {
				return 0;
			}
			return Math.round((count / sectorMax) * 100);
		}
	}
};
</script>

<style scoped>
.dashboard-root {
	width: 100%;
	max-width: 100%;
}

.dashboard-widget-card {
	border-radius: 4px;
	overflow: hidden;
}

.dashboard-widget-toolbar {
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
}

.sector-study-group__title {
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.study-name-cell {
	white-space: nowrap;
}
</style>

<style>
.sector-bar-wrap {
	min-width: 120px;
	padding: 4px 0;
}

.dashboard-data-table.v-data-table thead tr th {
	background-color: rgba(245, 124, 0, 0.1) !important;
	border-bottom: thin solid rgba(245, 124, 0, 0.35) !important;
	font-weight: 600 !important;
	font-size: 0.8125rem !important;
}

.dashboard-table-fullwidth {
	width: 100% !important;
}

.dashboard-table-fullwidth .v-data-table__wrapper {
	overflow-x: auto;
}
</style>
